import React, { useMemo, useState } from "react";
import { useTable, usePagination, useSortBy, useExpanded, useResizeColumns } from "react-table";
import { Table } from "reactstrap";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import DatePicker from "react-datepicker";
import { connect } from "react-redux";
import { dateStrToYmd, getFirstDayOfMonth } from "@/shared/helpers";
import { MONIES_AVAILABLE_POSTINGS_COLUMNS } from "./columns";
import { reducer, useEffectToDispatch, fetchApiData, Sorting } from "@/shared/apiTableHelper";

const queryClient = new QueryClient();

const defaultFrom = getFirstDayOfMonth();
const defaultTo = new Date();

export const initialState = {
  queryPageIndex: 0,
  queryPageSize: 10,
  totalCount: 0,
  queryPageFilter: {},
  queryPageSortBy: [{ id: "created_at", desc: true }],
  queryTrigger: 0,
};

const options = {
  dateRange: true,
};

const MoniesAvailablePostings = ({ program, organization }) => {
  const [startDate, setStartDate] = useState(defaultFrom);
  const [endDate, setEndDate] = useState(defaultTo);
  const [filter, setFilter] = useState({
    start_date: dateStrToYmd(defaultFrom),
    end_date: dateStrToYmd(new Date()),
  });
  const [useFilter, setUseFilter] = useState(false);

  const finalFilter = { ...filter };

  const onClickFilter = (reset = false) => {
    let data = {};
    if (options.dateRange) {
      data.start_date = dateStrToYmd(reset ? defaultFrom : startDate);
      data.end_date = dateStrToYmd(reset ? defaultTo : endDate);
    }

    onClickFilterCallback(data);
    if (reset) {
      setStartDate(defaultFrom);
      setEndDate(defaultTo);
    }
  };

  const onClickFilterCallback = (values) => {
    let change = false;
    if (options.dateRange) {
      if (
        finalFilter.start_date !== values.start_date ||
        finalFilter.end_date !== values.end_date
      ) {
        change = true;
      }
    }
    if (!change) {
      setUseFilter(false);
      return;
    }
    setFilter({
      start_date: values.start_date,
      end_date: values.end_date,
    });
    setUseFilter(true);
  };

  const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/program/${program.id}/monies-available-postings`;
  const { isLoading, error, data, isSuccess, isFetching } = useQuery(
    ["Name_MoniesAvailablePostings", apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
    () => fetchApiData(
      {
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        filter,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }
    ),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const columns = useMemo(() => MONIES_AVAILABLE_POSTINGS_COLUMNS, []);
  const tableData = Array.isArray(data?.results) ? data.results : Object.values(data?.results || {});
  const totalPageCount = Math.ceil(totalCount / queryPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable({
      columns,
      data: tableData,
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true,
      autoResetHiddenColumns: false,
      striped: true
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    // useFlexLayout,
  );

  const manualPageSize = [];
  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter });

  const onStartDateChange = (date) => {
    setStartDate(date);
  };

  const onEndDateChange = (date) => {
    setEndDate(date);
  };

  if (isLoading) {
    return (
      <div className="manager-billing__empty-state">
        <strong>Loading ledger...</strong>
        <span>Preparing the current funding activity.</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-billing__empty-state manager-billing__empty-state--error">
        <strong>Unable to load the ledger.</strong>
        <span>Please refresh the page or try a different program.</span>
      </div>
    );
  }
  
  if (isSuccess)
    return (
      <div className="manager-billing__ledger">
        <div className="manager-billing__filters">
          <div className="manager-billing__filter-fields">
            <div className="manager-billing__filter-field">
              <div className="form__form-group">
                <span className="form__form-group-label">From</span>
                <div className="form__form-group-field">
                  <div className="form__form-group-row">
                    <DatePicker
                      className="manager-billing__date-input"
                      dateFormat="MM/dd/yyyy"
                      selected={startDate}
                      onChange={onStartDateChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="manager-billing__filter-field">
              <div className="form__form-group">
                <span className="form__form-group-label">To</span>
                <div className="form__form-group-field">
                  <div className="form__form-group-row">
                    <DatePicker
                      className="manager-billing__date-input"
                      dateFormat="MM/dd/yyyy"
                      selected={endDate}
                      onChange={onEndDateChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="manager-billing__filter-actions">
            <button
              onClick={() => onClickFilter()}
              className="manager-home__send-btn manager-billing__filter-btn"
              type="button"
            >
              Apply Filter
            </button>
            <button
              onClick={() => onClickFilter(true)}
              className="manager-billing__secondary-btn manager-billing__filter-btn"
              type="button"
            >
              Reset
            </button>
          </div>
        </div>

        {isFetching && (
          <div className="manager-billing__table-state">Refreshing ledger...</div>
        )}

        {page.length > 0 ? (
          <>
            <div className="manager-billing__table-shell">
              <Table borderless size="sm" className="manager-billing__table table--stack-mobile" {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                          <span className="manager-billing__th-content">
                            {column.render("Header")}
                            {column.isSorted ? <Sorting column={column} /> : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="manager-billing__pagination">
              <ReactTablePagination
                page={page}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                pageIndex={pageIndex}
                pageCount={pageCount}
                setPageSize={setPageSize}
                manualPageSize={manualPageSize}
                dataLength={totalCount}
              />
            </div>
          </>
        ) : (
          <div className="manager-billing__empty-state">
            <strong>No ledger activity found.</strong>
            <span>Try adjusting the date range to review a different period.</span>
          </div>
        )}
      </div>
    );

  return null;
};

const TableWrapper = ({ organization, program }) => {
  if (!organization || !program) return "Loading...";
  return (
    <QueryClientProvider client={queryClient}>
      <MoniesAvailablePostings organization={organization} program={program} />
    </QueryClientProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    program: state.program,
    organization: state.organization,
  };
};
export default connect(mapStateToProps)(TableWrapper);
